import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { isMethodAvailable } from 'utils/getIsMethodActive';

import { PaymentType } from '../../Modulor/CurrencySelectContainer/types';
import { parseQuery, stringToInt } from '../../utils';
import type { UIPaymentGroups, UIPayoutGroups } from '../formatters';
import type { PayHubCashierContractsCashierSettingsResponse } from '../models';

export type CurrencyType = PaymentType.FIAT | PaymentType.CRYPTO | PaymentType.ON_RAMP;

export interface CurrencyBase {
  id: number;
  alpha3: string;
  isFiat: boolean;
  isOnRamp: boolean;
  currencyPrecision?: number;
  iconUrl?: string;
}

export interface SetWalletDataPayload {
  cashierSettings: PayHubCashierContractsCashierSettingsResponse;
  userBalance?: number;
  walletCurrencies: CurrencyBase[];
  cryptoCurrencies: CurrencyBase[];
  fiatCurrencies: CurrencyBase[];
  onRampCurrencies: CurrencyBase[];
  groups: UIPayoutGroups[] | UIPaymentGroups[];
  selectedCurrency: CurrencyBase | null;
  defaultFiatCurrency: string | null;
}

export interface WalletState {
  isMultiCurrency: boolean;
  userBalance: number;
  prevCurrentGroupId: number | null;
  currencyFlow: 'disabled' | 'fiat' | 'crypto' | 'onRamp' | 'mixed';
  walletCurrencies: CurrencyBase[];
  fiatCurrencies: CurrencyBase[];
  cryptoCurrencies: CurrencyBase[];
  onRampCurrencies: CurrencyBase[];
  selectedCurrency: CurrencyBase | null;
  defaultFiatCurrency: string | null;
  currentGroup: UIPaymentGroups | UIPayoutGroups | null;
  prevSelectedCurrency: {
    fiat: string | null;
    crypto: string | null;
    onRamp: string | null;
  };
  prevSelectedNetworkByCurrency: Record<string, UIPaymentGroups | UIPayoutGroups>;
}

export type CurrencyFlow = PaymentType.FIAT | PaymentType.CRYPTO | 'mixed';

const initialState: WalletState = {
  isMultiCurrency: false,
  currencyFlow: 'disabled',
  userBalance: 0,
  walletCurrencies: [],
  fiatCurrencies: [],
  cryptoCurrencies: [],
  onRampCurrencies: [],
  selectedCurrency: null,
  currentGroup: null,
  prevCurrentGroupId: null,
  defaultFiatCurrency: null,
  prevSelectedCurrency: {
    fiat: null,
    crypto: null,
    onRamp: null,
  },
  prevSelectedNetworkByCurrency: {},
};

export const walletSlice = createSlice({
  name: 'wallet',
  initialState,
  reducers: {
    setWalletData: (state, action: PayloadAction<SetWalletDataPayload>) => {
      const {
        cashierSettings,
        userBalance,
        walletCurrencies,
        groups,
        fiatCurrencies,
        cryptoCurrencies,
        onRampCurrencies,
        selectedCurrency,
        defaultFiatCurrency,
      } = action.payload;
      state.userBalance = userBalance || 0;
      state.defaultFiatCurrency = defaultFiatCurrency;
      state.selectedCurrency = selectedCurrency;
      if (!cashierSettings.multi_currency_cashier) {
        state.isMultiCurrency = false;
        state.currencyFlow = 'disabled';
        state.walletCurrencies = [];
        state.fiatCurrencies = [];
        state.cryptoCurrencies = [];
        state.onRampCurrencies = [];
        state.prevCurrentGroupId = null;
        state.prevSelectedCurrency = {
          fiat: null,
          crypto: null,
          onRamp: null,
        };
        state.prevSelectedNetworkByCurrency = {};
        return;
      }
      // ---------- multi-currency enabled ----------
      state.isMultiCurrency = true;
      state.prevCurrentGroupId = null;
      state.walletCurrencies = walletCurrencies;
      state.fiatCurrencies = fiatCurrencies;
      state.cryptoCurrencies = cryptoCurrencies;
      state.onRampCurrencies = onRampCurrencies;

      const hasFiat = state.fiatCurrencies.length > 0;
      const hasCrypto = state.cryptoCurrencies.length > 0;
      const hasOnRamp = state.onRampCurrencies.length > 0;

      // ---------- initial selected crypto method ----------
      const { pay_group = '' } = parseQuery(window.location.search);
      const methodFromQuery = groups.find(
        ({ id, name, enabled, disabledByCategory, restrictedUntil }) =>
          (id === stringToInt(pay_group as string) &&
            isMethodAvailable({
              enabled,
              disabledByCategory,
              restrictedUntil,
            })) ||
          (name === pay_group &&
            isMethodAvailable({
              enabled,
              disabledByCategory,
              restrictedUntil,
            })),
      );
      const lastUsedGroup = groups?.find((group) => isMethodAvailable(group) && group.last_used) || null;
      const firstGroup = groups?.find((group) => isMethodAvailable(group)) || groups?.[0] || null;

      const initialGroup = methodFromQuery || lastUsedGroup || firstGroup;

      const activeTypesCount = [hasFiat, hasCrypto, hasOnRamp].filter(Boolean).length;

      if (activeTypesCount > 1) {
        state.currencyFlow = 'mixed';
        state.currentGroup = initialGroup;
      } else if (hasCrypto) {
        state.currencyFlow = PaymentType.CRYPTO;
        state.currentGroup = initialGroup;
      } else if (hasFiat) {
        state.currencyFlow = PaymentType.FIAT;
      }

      // ---------- selected currency ----------
      if (selectedCurrency) {
        const type = selectedCurrency.isFiat ? PaymentType.FIAT : PaymentType.CRYPTO;
        state.prevSelectedCurrency[type] = selectedCurrency.alpha3;
      }
    },

    setSelectedCurrency: (state, action: PayloadAction<CurrencyBase | null>) => {
      state.selectedCurrency = action.payload;
    },

    setUserBalance: (state, action) => {
      state.userBalance = action.payload;
    },

    setCurrentGroup: (state, action) => {
      state.currentGroup = action.payload;
    },

    setPrevCurrentGroupId: (state, action) => {
      state.prevCurrentGroupId = action.payload;
    },

    setPrevSelectedCurrency: (state, action: PayloadAction<{ tab: PaymentType; alpha3: string | null }>) => {
      const { tab, alpha3 } = action.payload;
      state.prevSelectedCurrency[tab] = alpha3;
    },

    setPrevSelectedNetworkByCurrency: (
      state,
      action: PayloadAction<{ currency: string; network: UIPaymentGroups | UIPayoutGroups }>,
    ) => {
      const { currency, network } = action.payload;
      state.prevSelectedNetworkByCurrency[currency] = network;
    },

    clearWallet: (state) => {
      state.isMultiCurrency = false;
      state.userBalance = 0;
      state.currentGroup = null;
      state.walletCurrencies = [];
      state.fiatCurrencies = [];
      state.cryptoCurrencies = [];
      state.onRampCurrencies = [];
      state.selectedCurrency = null;
      state.prevCurrentGroupId = null;
      state.prevSelectedCurrency = {
        fiat: null,
        crypto: null,
        onRamp: null,
      };
      state.prevSelectedNetworkByCurrency = {};
    },
  },
});

export const {
  setWalletData,
  clearWallet,
  setSelectedCurrency,
  setUserBalance,
  setPrevCurrentGroupId,
  setCurrentGroup,
  setPrevSelectedCurrency,
  setPrevSelectedNetworkByCurrency,
} = walletSlice.actions;

// selectors
export const walletSelector = (state: { wallet: WalletState }) => state.wallet;
export const selectedCurrencySelector = (state: { wallet: WalletState }) => state.wallet.selectedCurrency;
export const walletCurrenciesSelector = (state: { wallet: WalletState }) => state.wallet.walletCurrencies;
export const isMultiCurrencySelector = (state: { wallet: WalletState }) => state.wallet.isMultiCurrency;
export const defaultFiatCurrencySelector = (state: { wallet: WalletState }) => state.wallet.defaultFiatCurrency;
export const fiatCurrenciesSelector = (state: { wallet: WalletState }) => state.wallet.fiatCurrencies;
export const cryptoCurrenciesSelector = (state: { wallet: WalletState }) => state.wallet.cryptoCurrencies;
export const onRampCurrenciesSelector = (state: { wallet: WalletState }) => state.wallet.onRampCurrencies;
export const currencyFlowSelector = (state: { wallet: WalletState }) => state.wallet.currencyFlow;
export const currentGroupSelector = (state: { wallet: WalletState }) => state.wallet.currentGroup;
export const prevCurrentGroupIdSelector = (state: { wallet: WalletState }) => state.wallet.prevCurrentGroupId;
export const prevSelectedCurrencySelector = (state: { wallet: WalletState }) => state.wallet.prevSelectedCurrency;
export const prevSelectedNetworkByCurrencySelector = (state: { wallet: WalletState }) =>
  state.wallet.prevSelectedNetworkByCurrency;

export default walletSlice.reducer;
